import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, Volume2, VolumeX } from 'lucide-react';
import GuidedHypnosisPlayer, { Starfield } from './GuidedHypnosisPlayer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const HYPNOS_AUTOSTART_KEY = 'hypnos-autostart';

export default function GuidedHypnosis() {
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState('fun');
  const [length, setLength] = useState(7);
  const [worry, setWorry] = useState('');
  const [voiceOn, setVoiceOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => axios.get(`${API}/hypnos/guided/history`, { withCredentials: true })
    .then((r) => setHistory(r.data?.sessions || [])).catch(() => {});

  const start = async (k = kind, len = length, text = worry) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/hypnos/guided/generate`, { kind: k, length_min: len, worry: text }, { withCredentials: true, timeout: 90000 });
      setSession(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Corgin tappade tråden. Prova igen 💜');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/hypnos/guided/kinds`, { withCredentials: true }).then((r) => setKinds(r.data?.kinds || [])).catch(() => {});
    loadHistory();
    if (sessionStorage.getItem(HYPNOS_AUTOSTART_KEY) === 'sos') {
      sessionStorage.removeItem(HYPNOS_AUTOSTART_KEY);
      start('sos', 3, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (k) => { setKind(k.id); setLength(k.default_len); };
  const current = kinds.find((k) => k.id === kind);

  return (
    <div className="relative rounded-3xl overflow-hidden p-5 sm:p-7" style={{ background: 'radial-gradient(ellipse at top left, #2a1d4f 0%, #140e2b 60%, #0a0716 100%)' }} data-testid="hypnos-guided">
      <Starfield />
      <div className="relative z-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-black">Din trygga plats</div>
            <h2 className="font-['Fredoka'] text-3xl sm:text-4xl text-white mt-1">Vägledd hypnos 🌙</h2>
            <p className="text-sm text-white/60 mt-1 max-w-md">Skriv vad som känns tungt. Corgin skriver en lugn resa åt dig och läser den högt.</p>
          </div>
          <button onClick={() => start('sos', 3, '')} disabled={loading}
            className="rounded-full px-5 py-3 font-black text-[#0b1b33] bg-[#A2D2FF] shadow-[0_0_30px_rgba(162,210,255,0.5)] hover:scale-105 transition-transform flex items-center gap-2 disabled:opacity-50"
            data-testid="hypnos-sos-btn">🫧 SOS · 3 min</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-6" data-testid="hypnos-guided-kinds">
          {kinds.length === 0 && [0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-white/5 border border-white/10 animate-pulse" data-testid={`hypnos-kind-skeleton-${i}`} />
          ))}
          {kinds.map((k) => (
            <button key={k.id} onClick={() => pick(k)} data-testid={`hypnos-kind-${k.id}`}
              className={`text-left rounded-2xl p-3 border transition-[background-color,border-color,transform] hover:-translate-y-0.5 ${kind === k.id ? 'bg-white/15' : 'bg-white/5 border-white/10'}`}
              style={kind === k.id ? { borderColor: k.accent, boxShadow: `0 0 24px ${k.accent}33` } : undefined}>
              <div className="text-2xl">{k.emoji}</div>
              <div className="font-bold text-white text-sm mt-1 leading-tight">{k.title}</div>
              <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{k.subtitle}</div>
            </button>
          ))}
        </div>

        <textarea value={worry} onChange={(e) => setWorry(e.target.value)} maxLength={600} rows={3}
          placeholder={current?.placeholder || 'Vad känns jobbigt just nu?'}
          className="mt-4 w-full rounded-2xl bg-white/5 border border-white/10 focus:border-[#C8B6FF] text-white placeholder:text-white/30 p-3 text-sm outline-none resize-none"
          data-testid="hypnos-guided-worry" />

        <div className="flex flex-wrap items-center gap-3 mt-3">
          <div className="flex rounded-full bg-white/5 border border-white/10 p-1" data-testid="hypnos-guided-lengths">
            {[3, 7, 12].map((m) => (
              <button key={m} onClick={() => setLength(m)} data-testid={`hypnos-length-${m}`}
                className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${length === m ? 'bg-[#C8B6FF] text-[#1a1033]' : 'text-white/60 hover:text-white'}`}>{m} min</button>
            ))}
          </div>
          <button onClick={() => setVoiceOn(!voiceOn)} data-testid="hypnos-guided-voice-pref"
            className="rounded-full px-4 py-2 text-sm text-white/70 border border-white/10 flex items-center gap-2 hover:bg-white/10 transition-colors">
            {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} {voiceOn ? 'Röst på' : 'Bara text'}
          </button>
          <button onClick={() => start()} disabled={loading || (kind === 'fun' && !worry.trim())} data-testid="hypnos-guided-start"
            className="ml-auto rounded-full px-6 py-3 font-bold text-[#1a1033] bg-gradient-to-r from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] hover:scale-105 transition-transform disabled:opacity-40 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Börja resan
          </button>
        </div>

        {loading && (
          <div className="mt-4 text-sm text-white/60 italic flex items-center gap-2" data-testid="hypnos-guided-loading">
            <span className="animate-bounce">🐶</span> Corgin skriver din resa… andas lugnt under tiden
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-6 pt-4 border-t border-white/10" data-testid="hypnos-guided-history">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-black mb-2">Dina senaste resor</div>
            <div className="flex flex-wrap gap-2">
              {history.slice(0, 6).map((h) => (
                <span key={h.id} className="text-xs text-white/70 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                  {h.emoji} {h.title} · {h.length_min} min {h.completed ? '✓' : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {session && (
        <GuidedHypnosisPlayer session={session} voiceOn={voiceOn} onClose={() => { setSession(null); loadHistory(); }} />
      )}
    </div>
  );
}
